import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { ReactNode, useCallback, useEffect, useState } from "react";

type MobileDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  className?: string;
};

const ANIMATION_DURATION = 300; // ms (match animation duration in tailwind config)

export function MobileDrawer({ open, onOpenChange, children, className }: MobileDrawerProps) {
  const [isVisible, setIsVisible] = useState(open);
  const [isExiting, setIsExiting] = useState(false);

  const delayOnOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setIsExiting(true);
        setTimeout(() => {
          onOpenChange(open);
          setIsExiting(false);
          setIsVisible(false);
        }, ANIMATION_DURATION);
      } else {
        onOpenChange(open);
      }
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsExiting(false);
    } else if (isVisible) {
      delayOnOpenChange(false);
    }
  }, [open, isVisible, delayOnOpenChange]);

  if (!isVisible) return null;

  return (
    <Dialog.Root open={open} onOpenChange={delayOnOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={clsx("fixed inset-0 bg-black/40 z-50")} />
        <Dialog.Content
          className={clsx(
            "fixed left-0 right-0 bottom-0 z-50 w-full max-h-[80vh] rounded-t-2xl bg-white p-4 flex flex-col border-none outline-none",
            isExiting ? "animate-drawer-slide-down" : "animate-drawer-slide-up",
            className
          )}
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
